import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Lock, ArrowUpRight } from "lucide-react";
import { IMAGES } from "../data/content";
import { CountUp, MOTION } from "./Reveal";

/**
 * SampleCampaignPreview — the artifact both HowItWorks columns point at: a
 * finished campaign as a tilted phone. Sits centered under the student and
 * donor journeys and links to the sample pool; a typed text link beneath
 * it does the captioning (audit L2: no second handwritten note here).
 *
 * Sized as a supporting illustration, not a hero (audit: trim structural
 * height): ~280px screen, 16:10 photo, no status bar. Persona mirrors the
 * "Zohran M." demo campaign ($2,000 goal / $1,750 raised) so a click lands
 * on a matching page; the $500 beta cap is footnoted inside the screen.
 */
export const SampleCampaignPreview = () => (
  <div className="mt-16 flex flex-col items-center gap-6" data-testid="sample-campaign-preview">
    <Link
      to="/demo#campaigns"
      aria-label="View sample student campaigns"
      className="group relative block -rotate-2 transition-transform duration-500 ease-out hover:rotate-0 focus-visible:rotate-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tangerine focus-visible:ring-offset-4 focus-visible:ring-offset-bone"
      data-testid="sample-campaign-link"
    >
      <div className="relative rounded-[2.5rem] bg-ink p-2 shadow-[0_25px_60px_-15px_rgba(26,23,20,0.35)]">
        <div aria-hidden="true" className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-ink" />
        <div className="relative w-[280px] overflow-hidden rounded-[2rem] bg-white pt-7 sm:w-[300px]">
          <div className="mx-3 mb-2.5 flex items-center gap-1.5 rounded-full bg-beige/60 px-3 py-1 text-[10px] text-olive">
            <Lock className="h-3 w-3" strokeWidth={2} />
            <span>myrichaunt.org/campaigns/zohran-m</span>
          </div>

          <div className="relative mx-3 aspect-[16/10] overflow-hidden rounded-xl bg-beige">
            <img src={IMAGES.group} alt="Sample student portrait" loading="lazy" className="h-full w-full object-cover" />
            <span className="absolute right-2.5 top-2.5 rounded-full bg-white/95 px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-ink shadow-sm">
              A sample
            </span>
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/95 shadow-lg ring-1 ring-black/5 transition-transform duration-300 group-hover:scale-105">
                <Play className="ml-0.5 h-4 w-4 fill-ink text-ink" strokeWidth={0} />
              </div>
            </div>
            <span className="absolute bottom-2.5 left-2.5 rounded-full bg-black/70 px-2 py-0.5 text-[9px] font-medium text-white backdrop-blur-sm">1:24</span>
          </div>

          <div className="px-3.5 pb-3.5 pt-3">
            <p className="text-[9px] font-medium uppercase tracking-[0.14em] text-olive">UC Berkeley · Junior</p>
            <h4 className="mt-0.5 font-serif text-xl leading-tight text-ink">Zohran M.</h4>
            <p className="mt-1 text-xs leading-snug text-olive">A semester studying in Nairobi would change how I see the world, and my future.</p>

            <div className="mt-3">
              <div className="flex items-baseline justify-between text-[10px]">
                <span className="font-semibold text-ink">
                  <CountUp to={1750} prefix="$" /> <span className="font-normal text-olive">raised</span>
                </span>
                <span className="text-olive">of $2,000 goal</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-beige">
                <motion.div
                  className="h-full rounded-full bg-tangerine"
                  initial={{ width: 0 }}
                  whileInView={{ width: "87.5%" }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 1.4, ease: MOTION.EASE, delay: 0.2 }}
                />
              </div>
            </div>

            <div className="mt-3 flex items-center justify-center gap-2 rounded-full bg-ink px-4 py-2 text-xs font-medium text-bone transition-colors group-hover:bg-forest">
              <span aria-hidden="true">♥</span>
              <span>Give</span>
            </div>

            <p className="mt-2.5 border-t border-dashed border-ink/15 pt-2 text-center text-[9px] font-medium uppercase tracking-[0.12em] leading-relaxed text-olive">
              Beta cap <span className="text-ink">$500 per student</span> · goal at pilot scale
            </p>
          </div>
        </div>
      </div>
    </Link>

    <Link
      to="/demo#campaigns"
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink underline-offset-4 hover:underline"
      data-testid="sample-campaign-caption-link"
    >
      View the sample campaigns
      <ArrowUpRight className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
    </Link>
  </div>
);
